import {
  Autocomplete as MuiAutocomplete,
  AutocompleteProps as MuiAutocompleteProps,
  Paper,
  Popper,
  styled,
} from "@mui/material";
import {
  autocompleteClasses,
  AutocompleteInputChangeReason,
  createFilterOptions,
} from "@mui/material/Autocomplete";
import { useEffect, useMemo, useState } from "react";

import { Box } from "@/Components/v2/atoms/Box";
import { DropdownButton } from "@/Components/v2/atoms/Button";
import { colors } from "@/Components/v2/utils/brand-colors";

import HierarchicalRenderOption from "./HierarchicalRenderOption";
import { EnhancedOption, HierarchicalOption } from "./types";

type AutocompleteProps<
  Value = any,
  Multiple extends boolean | undefined = boolean | undefined,
  DisableClearable extends boolean | undefined = boolean | undefined,
  FreeSolo extends boolean | undefined = boolean | undefined,
  ChipComponent extends React.ElementType<any> = React.ElementType<any>,
> = Omit<
  MuiAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>,
  "options" | "onChange"
> & {
  options: HierarchicalOption[];
  defaultId?: number;
  onChange?: (value: EnhancedOption | null) => void;
};

const StyledAutocomplete = styled(MuiAutocomplete)(() => ({}));

const StyledPopper = styled(Popper)(({ theme }) => ({
  [`& .${autocompleteClasses.option}`]: {
    paddingLeft: `${theme.spacing(2)} !important`,
    fontWeight: 600,
    backgroundColor: theme.palette.background.paper,
    minHeight: `${theme.spacing(6)} !important`,
  },

  [`&& .${autocompleteClasses.groupLabel}`]: {
    lineHeight: `${theme.spacing(5)}`,
  },
}));

const toOptions = (
  hierarchicalOption: HierarchicalOption,
  depth: number = 0,
  parentId: number | null = null
): EnhancedOption[] => {
  const {
    id,
    label,
    value,
    children = [],
    alwaysOpen = false,
    selectable,
    endAdornment,
  } = hierarchicalOption;
  const flattenChildren = children.flatMap((child) => toOptions(child, depth + 1, id));
  const option = {
    id,
    label,
    value,
    depth,
    parentId,
    alwaysOpen,
    matchTerms: [label].concat(flattenChildren.map((obj) => obj.label)),
    hasChildren: children.length > 0,
    ...(selectable !== undefined && { selectable }),
    ...(endAdornment !== undefined && { endAdornment }),
  };
  return [option].concat(flattenChildren);
};

export const HierarchicalAutocomplete: React.FC<AutocompleteProps> = (props) => {
  const { options: hierarchicalData, defaultId = 0, value, onChange } = props;

  const optionsList = useMemo(
    () => hierarchicalData.flatMap((hierarchicalOption) => toOptions(hierarchicalOption)),
    [hierarchicalData]
  );
  const alwaysOpenIds = useMemo(
    () => optionsList.filter((option) => option.alwaysOpen).map((option) => option.id),
    [optionsList]
  );

  const isAllAlwaysOpen = useMemo(
    () => optionsList.every((option) => option.alwaysOpen),
    [optionsList]
  );

  const selectedOption = optionsList.find((option) => option.value === value);

  const [isAllExpanded, setIsAllExpanded] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [expandedItems, setExpandedItems] = useState<number[]>(alwaysOpenIds);

  const options = useMemo(
    () =>
      optionsList.filter(
        (option) => option.parentId === null || expandedItems.includes(option.parentId)
      ),
    [optionsList, expandedItems]
  );

  const defaultSelectedOption = optionsList.find((option) => option.id === defaultId);

  const handleInputChange = (value: string, reason: AutocompleteInputChangeReason) => {
    if (reason === "input") {
      setSearchTerm(value);
    }
  };

  const handleChange = (value: EnhancedOption | null) => {
    if (onChange !== undefined) {
      if (value) {
        // Prevent selection of non-selectable options
        if (value.selectable === false) {
          return;
        }
        onChange(value);
      } else {
        onChange(null);
      }
    }
  };

  const toggleExpand = (id: number) => {
    setExpandedItems((current) => {
      if (current.includes(id)) {
        // If the item is currently expanded, collapse it and its descendants
        const allDescendants = findAllDescendantIds(id, optionsList);
        return current.filter((item) => item !== id && !allDescendants.includes(item));
      } else {
        // If the item is currently collapsed, expand it
        return [...current, id];
      }
    });
  };

  const findAllDescendantIds = (parentId: number, options: any[]): number[] => {
    let descendants: number[] = [];
    options.forEach((option) => {
      if (option.parentId === parentId) {
        descendants.push(option.id);
        if (options.some((o) => o.parentId === option.id)) {
          // Recursively find descendants if this option is a parent
          descendants = [...descendants, ...findAllDescendantIds(option.id, options)];
        }
      }
    });
    return descendants;
  };

  const toggleExpandAll = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();

    if (isAllExpanded) {
      setExpandedItems(alwaysOpenIds);
    } else {
      setExpandedItems(optionsList.map((option) => option.id));
    }
    setIsAllExpanded(!isAllExpanded);
  };

  useEffect(() => {
    let idsToExpand = alwaysOpenIds; // Default to alwaysOpenIds

    if (searchTerm) {
      const matchedItems = optionsList.filter((option) =>
        option.matchTerms.some((term) => term.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      const parentIds = matchedItems
        .filter((item) => item.parentId !== null)
        .map((item) => item.parentId as number);

      // Combine parentIds with alwaysOpenIds using Set for uniqueness
      idsToExpand = Array.from(new Set([...parentIds, ...alwaysOpenIds]));
    }

    setExpandedItems(idsToExpand);
  }, [searchTerm, alwaysOpenIds]);

  useEffect(() => {
    const selectedOption = optionsList.find((option) => option.value === value);
    // Expand parents when a new value is selected
    if (selectedOption) {
      setExpandedItems((current) => {
        const parentIds = findAllParentIds(selectedOption.id, optionsList);
        return Array.from(new Set([...current, ...parentIds]));
      });
    }
  }, [value, optionsList]);

  const findAllParentIds = (id: number, options: any[]): number[] => {
    const parents: number[] = [];
    let parentId = options.find((option) => option.id === id)?.parentId;

    while (parentId !== null && parentId !== undefined) {
      parents.push(parentId);
      parentId = options.find((option) => option.id === parentId)?.parentId;
    }

    return parents;
  };

  return (
    <StyledAutocomplete
      isOptionEqualToValue={(option, value) => {
        const typedOption = option as EnhancedOption;

        if (typeof value === "string") {
          const result = typedOption.value === value;

          return result;
        }

        const typedValue = value as EnhancedOption;
        if (!typedValue) {
          return false;
        }

        const result = typedOption.id === typedValue.id;

        return result;
      }}
      PopperComponent={StyledPopper}
      PaperComponent={({ children }) => {
        return (
          <Paper sx={{ backgroundImage: "none" }}>
            {!isAllAlwaysOpen && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  borderBottom: 1,
                  padding: 1,
                  borderColor: colors.dropdownBorder,
                  marginX: 1,
                }}
              >
                <DropdownButton onMouseDown={toggleExpandAll}>
                  {isAllExpanded ? "Close All" : "Expand All"}
                </DropdownButton>
              </Box>
            )}
            {children}
          </Paper>
        );
      }}
      {...props}
      defaultValue={defaultSelectedOption}
      value={selectedOption}
      options={options}
      getOptionLabel={(option: unknown) => {
        const typedOption = option as EnhancedOption;
        return typedOption.label;
      }}
      renderOption={(props, option) => {
        const typedOption = option as EnhancedOption;
        return (
          <HierarchicalRenderOption
            {...props}
            key={typedOption.id}
            option={typedOption}
            expandedItems={expandedItems}
            toggleExpand={toggleExpand}
          />
        );
      }}
      onInputChange={(_, value, reason) => handleInputChange(value, reason)}
      onChange={(_, value) => handleChange(value as EnhancedOption)}
      getOptionDisabled={(option) => {
        const typedOption = option as EnhancedOption;
        return typedOption.selectable === false;
      }}
      filterOptions={createFilterOptions({
        stringify: (option) => {
          const typedOption = option as EnhancedOption;
          return typedOption.matchTerms.join("//");
        },
      })}
    />
  );
};

export default HierarchicalAutocomplete;
